import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import BriefListingCard from 'components/BriefListingCard/BriefListingCard';
import JobCard from 'components/JobCard/JobCard';
import { parseAriJobId } from 'components/JobCard/saveJobBody';
import useFetchJobStates from 'components/JobCard/useFetchJobStates';
import noActivity from './no-activity.png';
import JobsBoardBanner from '../../containers/Ads/JobsBoardBanner/JobsBoardBanner';
import SignUp from 'components/SignUp/SignUp';
import useAuth from 'hooks/useAuth';
import BrowseMoreJobs from 'components/BrowseMoreJobs/BrowseMoreJobs';
import JobsBestMatchCard from 'components/JobsBestMatchCard/JobsBestMatchCard';
import JobsEmptyState from 'components/JobsEmptyState/JobsEmptyState';
import JobsTimeline from 'components/JobsTimeline/JobsTimeline';
import styles from './BriefListings.module.scss';
import WebOnly from 'components/NativeApp/WebOnly';

/** Partner ads after the job at index 1, then every 15th job (14, 29, 44…). */
export const shouldInjectJobsBoardAd = (jobIndex) =>
  jobIndex === 1 || (jobIndex + 1) % 15 === 0;

/** Best match card sits after the job at this index (inside the first batch). */
export const JOBS_BEST_MATCH_CARD_INDEX = 4;

/** Guest browse-more box after the last job of the first API batch (limit 10). */
export const JOBS_BROWSE_MORE_INDEX = 9;

/** Twine and external_jobs ids on the board, for "Viewed" / "Applied". */
export const getJobStateIds = (briefs, timeline, hasTimeline) => {
  if (!hasTimeline) {
    return { jobIds: briefs.map((brief) => brief.id), ariJobIds: [] };
  }
  const twine = [...timeline.featured];
  const ariJobIds = [];
  timeline.days.forEach((day) => {
    twine.push(...day.twine);
    ((day.external && day.external.jobs) || []).forEach((job) => {
      const id = parseAriJobId(job.id);
      if (id != null) ariJobIds.push(id);
    });
  });
  return { jobIds: twine.map((brief) => brief.id), ariJobIds };
};

const BriefListings = ({
  briefs,
  loaded,
  loading: _loading,
  selectedRole,
  paid,
  onClickRole,
  onClickLocation,
  country,
  partners,
  loadedPartners,
  loadingPartners,
  pageType,
  auth,
  filterCount,
  onBestMatchNavigate,
  showBestMatchCard,
  filter,
  suggestedRoles,
  pageRole,
  pageCountry,
  pageCity,
  emptyFocusChipKey,
  onEmptyFocusChipKeyConsumed,
  onEmptyRemoveChip,
  onFiltersReset,
  timeline,
  timelineToday,
  expandedExternalDays,
  onShowAllExternal,
  ...rest
}) => {
  const { user } = useAuth();

  let jobsBoardBannerCount = 0;
  const maxBannerCount = 5;

  const hasTimeline = Boolean(
    timeline && (timeline.featured.length > 0 || timeline.days.length > 0)
  );

  useFetchJobStates(
    loaded
      ? getJobStateIds(briefs, timeline, hasTimeline)
      : { jobIds: [], ariJobIds: [] }
  );

  const showJobsEmptyState =
    paid &&
    loaded &&
    briefs.length === 0 &&
    !hasTimeline &&
    filter &&
    typeof onEmptyRemoveChip === 'function' &&
    typeof onFiltersReset === 'function';

  return (
    <div {...rest}>
      {loaded ? (
        <div>
          {hasTimeline ? (
            <JobsTimeline
              timeline={timeline}
              today={timelineToday}
              expandedDays={expandedExternalDays}
              onShowAllExternal={onShowAllExternal}
              onClickRole={onClickRole}
              onClickLocation={onClickLocation}
              partners={partners}
              loadedPartners={loadedPartners}
              loadingPartners={loadingPartners}
              pageType={pageType}
              auth={auth}
              filterCount={filterCount}
              onBestMatchNavigate={onBestMatchNavigate}
              showBestMatchCard={showBestMatchCard}
              country={country}
            />
          ) : briefs.length > 0 ? (
            <div>
              {briefs.map((brief, idx) => {
                const injectAd =
                  shouldInjectJobsBoardAd(idx) &&
                  jobsBoardBannerCount < maxBannerCount;

                if (injectAd) {
                  jobsBoardBannerCount++;
                }

                const isLastBrief = idx === briefs.length - 1;
                const showBrowseMore =
                  auth === 'guest' &&
                  (idx === JOBS_BROWSE_MORE_INDEX ||
                    (briefs.length <= JOBS_BROWSE_MORE_INDEX && isLastBrief));

                const briefListing = (
                  <JobCard
                    brief={brief}
                    openInNewTab
                    showJobState
                    key={brief.id}
                    onClickRole={onClickRole}
                    onClickLocation={onClickLocation}
                  />
                );

                return (
                  <Fragment key={`briefListing-${brief.id}`}>
                    {briefListing}
                    {injectAd && (
                      <JobsBoardBanner
                        partners={partners}
                        loadedPartners={loadedPartners}
                        loadingPartners={loadingPartners}
                      />
                    )}
                    {showBestMatchCard &&
                      idx === JOBS_BEST_MATCH_CARD_INDEX && (
                        <JobsBestMatchCard
                          pageType={pageType}
                          auth={auth}
                          filterCount={filterCount}
                          onBestMatchNavigate={onBestMatchNavigate}
                        />
                      )}
                    {showBrowseMore && (
                      <>
                        {idx === JOBS_BROWSE_MORE_INDEX && (
                          <div
                            className={styles.batchDivider}
                            data-testid="jobs-batch-divider"
                          >
                            <hr className={styles.batchDividerRule} />
                            <span className={styles.batchDividerLabel}>
                              End of the first batch of jobs
                            </span>
                            <hr className={styles.batchDividerRule} />
                          </div>
                        )}
                        <BrowseMoreJobs placement="mobile" country={country} />
                      </>
                    )}
                  </Fragment>
                );
              })}
            </div>
          ) : showJobsEmptyState ? (
            <>
              <JobsEmptyState
                filter={filter}
                suggestedRoles={suggestedRoles}
                pageType={pageType}
                pageRole={pageRole}
                pageCountry={pageCountry}
                pageCity={pageCity}
                focusChipKey={emptyFocusChipKey}
                onFocusChipKeyConsumed={onEmptyFocusChipKeyConsumed}
                onRemoveChip={onEmptyRemoveChip}
                onFiltersReset={onFiltersReset}
              />
              {auth === 'guest' && (
                <BrowseMoreJobs placement="mobile" country={country} />
              )}
            </>
          ) : (
            <div className="mt++ text-center">
              <img src={noActivity} alt="Twine" />
              <p className="mt++">
                No
                {selectedRole !== null
                  ? ` ${selectedRole.name.toLowerCase()}`
                  : ''}{' '}
                {paid ? 'jobs' : 'collaborations'} found.
              </p>
              {!user ? (
                <>
                  <p>
                    Sign up to Twine below for free and get notified instantly
                    when jobs are available.
                  </p>
                  <SignUp
                    redirect={'onboarding'}
                    redirectFromGoogle={'signup'}
                    defaultAccountType={'seller'}
                  />
                  <BrowseMoreJobs placement="mobile" country={country} />
                </>
              ) : (
                <>
                  <p>
                    Turn on <a href="/settings">job alert notifications</a> to
                    be notified when the next job is posted.
                  </p>
                  <WebOnly>
                    <p>
                      <a href="/pricing/creatives">Upgrade to Twine Pro</a> to
                      increase your chances of winning work.
                    </p>
                  </WebOnly>
                </>
              )}
            </div>
          )}
        </div>
      ) : (
        [...Array(3)].map((_, idx) => (
          <BriefListingCard brief={null} key={idx} />
        ))
      )}
    </div>
  );
};

BriefListings.propTypes = {
  briefs: PropTypes.array,
  loaded: PropTypes.bool,
  loading: PropTypes.bool, // retained for callers; load-more UI lives in ProjectsBase
  selectedRole: PropTypes.object,
  paid: PropTypes.bool,
  onClickRole: PropTypes.func,
  onClickLocation: PropTypes.func,
  country: PropTypes.string,
  partners: PropTypes.array,
  loadedPartners: PropTypes.bool,
  loadingPartners: PropTypes.bool,
  pageType: PropTypes.oneOf(['jobs', 'role', 'country']),
  auth: PropTypes.oneOf(['guest', 'logged_in']),
  filterCount: PropTypes.number,
  onBestMatchNavigate: PropTypes.func,
  showBestMatchCard: PropTypes.bool,
  filter: PropTypes.object,
  suggestedRoles: PropTypes.array,
  pageRole: PropTypes.string,
  pageCountry: PropTypes.string,
  pageCity: PropTypes.string,
  emptyFocusChipKey: PropTypes.string,
  onEmptyFocusChipKeyConsumed: PropTypes.func,
  onEmptyRemoveChip: PropTypes.func,
  onFiltersReset: PropTypes.func,
  /** Newest first on /jobs: buildJobsTimeline() result, rendered by day. */
  timeline: PropTypes.object,
  timelineToday: PropTypes.string,
  expandedExternalDays: PropTypes.object,
  onShowAllExternal: PropTypes.func,
};

BriefListings.defaultProps = {
  pageType: 'jobs',
  auth: 'guest',
  filterCount: 0,
  onBestMatchNavigate: undefined,
  showBestMatchCard: true,
  filter: null,
  suggestedRoles: [],
  pageRole: null,
  pageCountry: null,
  pageCity: null,
  emptyFocusChipKey: null,
  onEmptyFocusChipKeyConsumed: undefined,
  onEmptyRemoveChip: undefined,
  onFiltersReset: undefined,
  timeline: null,
  timelineToday: null,
  expandedExternalDays: {},
  onShowAllExternal: undefined,
};

export default BriefListings;
